import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Lightbulb } from "lucide-react";

const CLOSE_ANIMATION_MS = 220;

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  width?: number;
  closeOnOverlayClick?: boolean;
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title = "Insights",
  children,
  width = 380,
  closeOnOverlayClick = true,
}) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      return;
    }
    if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, CLOSE_ANIMATION_MS);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!shouldRender) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [shouldRender, onClose]);

  if (!shouldRender) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div
      className={`app-drawer-overlay${isClosing ? " app-drawer-overlay--closing" : ""}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <aside
        className={`app-drawer${isClosing ? " app-drawer--closing" : ""}`}
        style={{ width }}
      >
        <div className="app-drawer-header">
          <div className="app-drawer-title-wrap">
            <Lightbulb size={16} strokeWidth={1.75} className="app-drawer-title-icon" />
            <h3 className="app-drawer-title">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="app-drawer-close"
            aria-label="Close drawer"
            data-testid="BTN_drawer_close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="app-drawer-body">{children}</div>
      </aside>
    </div>,
    document.body,
  );
};

export default Drawer;
